import { useQuery } from "@apollo/client";
import { useState } from "react";
import { ArtifactType, GoalState } from "types/graphql-schema";

import ArtifactDropdownMenu from "@apps/artifact/artifact-dropdown-menu";
import ActionItemAssignee from "@apps/artifact/components/artifact-assignee";
import ArtifactDueDate from "@apps/artifact/components/artifact-due-date";
import GoalParticipants from "@apps/artifact/components/goal-participants";
import ArtifactRecognitionRecipients from "@apps/artifact/components/recognition-recipients";
import ObjectiveCheckinDialog from "@apps/checkin-dialog/checkin-dialog";
import GoalProgress from "@apps/goal-progress/goal-progress";
import { ButtonSize } from "@components/button/button-constants";
import DraftLabel from "@components/draft-label/draft-label";
import Loading from "@components/loading/loading";
import { artifactType } from "@helpers/constants";

import getWysiwygArtifactQuery from "./graphql/get-wysiwyg-artifact-query";

/**
 * Right-side toolbar inside an artifact embed: due date, progress / goal
 * participants / assignee / recipients, then the "..." dropdown. The
 * description editor and inline-comment threads are no longer rendered, so
 * the toggle/remove props for those are gone.
 */
const ArtifactInfos = ({
  syncedArtifact,
  loading,
  onDelete,
  onSavedComment,
}: {
  syncedArtifact: {
    id: number;
    artifactType: ArtifactType;
  };
  loading: boolean;
  onDelete: () => void;
  onSavedComment: () => void;
}) => {
  const [isShowingCheckinDialog, setIsShowingCheckinDialog] = useState(false);
  const isActionItem = syncedArtifact.artifactType === artifactType.actionItem;
  const isGoal = syncedArtifact.artifactType === artifactType.goal;
  const { data } = useQuery(getWysiwygArtifactQuery, {
    fetchPolicy: "cache-only", // rely only on cached content to render this view
    variables: { artifactId: Number(syncedArtifact.id) },
  });
  const artifact = data?.artifact || syncedArtifact;

  return (
    <div className="flex items-center gap-1">
      {loading && (
        <div className="flex">
          <Loading mini size="4" />
        </div>
      )}
      {isShowingCheckinDialog && (
        <ObjectiveCheckinDialog
          artifact={artifact}
          onClose={() => setIsShowingCheckinDialog(false)}
        />
      )}

      {artifact.id && isGoal && (artifact as any).state === GoalState.Draft && (
        // Cap pill height at 20px so it lines up with the title row and the
        // other 20x20 right-side controls.
        <DraftLabel className="h-5 inline-flex items-center" />
      )}
      {artifact.id && (isActionItem || isGoal) && (
        <div className="hidden @md/wysiwyg-artifact:flex items-center text-xs">
          <ArtifactDueDate artifact={artifact} />
        </div>
      )}
      {artifact.id && isGoal && (
        <GoalProgress artifact={artifact} withPopover={true} />
      )}

      {artifact.id && isActionItem && (
        <div
          contentEditable={false}
          className="text-gray-400 hover:text-gray-600 hidden @md/wysiwyg-artifact:flex"
        >
          <ActionItemAssignee
            artifact={artifact}
            showName={false}
            size={ButtonSize.small}
          />
        </div>
      )}
      {artifact.id && artifact.artifactType === ArtifactType.Recognition && (
        <div
          contentEditable={false}
          className="text-gray-400 hover:text-gray-600 hidden @md/wysiwyg-artifact:flex items-center"
        >
          <ArtifactRecognitionRecipients artifact={artifact} />
        </div>
      )}
      {artifact.id && isGoal && (
        <div
          contentEditable={false}
          className="text-gray-400 hover:text-gray-600 hidden @md/wysiwyg-artifact:flex items-center"
        >
          <GoalParticipants artifact={artifact} />
        </div>
      )}

      <div className="flex items-center">
        <ArtifactDropdownMenu
          isInWysiwyg
          artifact={artifact}
          size={ButtonSize.small}
          onDelete={() => onDelete()}
          onSavedComment={onSavedComment}
        />
      </div>
    </div>
  );
};

export default ArtifactInfos;
